import { array, number, object, string } from "yup";
import { dateMax, dateSameOrAfter, stringMax } from "./utils";
import {
  BALANCE_DETAIL_AMOUNT_KEYS,
  DETAILS_TOLERANCE,
} from "../utils/constants/balances";

const balanceDetailsShape = object({
  ...Object.fromEntries(
    BALANCE_DETAIL_AMOUNT_KEYS.map((key) => [
      key,
      number()
        // Un champ laissé vide est une absence, pas un nombre invalide.
        .transform((value, original) => (original === "" ? null : value))
        .min(0, "Un sous-montant ne peut pas être négatif")
        .nullable(),
    ]),
  ),
  energy: string().nullable(),
});

// Les sous-montants sont facultatifs, mais leur somme tient dans le montant.
const getBalanceDetailsSchema = () =>
  balanceDetailsShape.test(
    "details-within-amount",
    "La somme des sous-montants dépasse le montant",
    function (details) {
      if (!details) return true;
      const total = BALANCE_DETAIL_AMOUNT_KEYS.reduce(
        (sum, key) => sum + (Number(details[key]) || 0),
        0,
      );
      return (
        total <= Math.abs(Number(this.parent.montant) || 0) + DETAILS_TOLERANCE
      );
    },
  );

export const balanceDetailsIV = Object.freeze({
  ...Object.fromEntries(BALANCE_DETAIL_AMOUNT_KEYS.map((key) => [key, ""])),
  energy: "",
});

export const getBalanceSchema = (balanceTypes) => {
  return object({
    date_transac: dateMax().required(" "),
    refacturation_values: array()
      .of(
        object({
          tenant_id: string().required(" "),
          date: dateMax().required(" "),
        }),
      )
      .optional(),
    // Période éventuelle associée à la balance (facture d'énergie, eau, etc.)
    // Les champs sont obligatoires si le type a has_period (ils sont affichés)
    periode_debut: dateMax().when("type", {
      is: (type) => {
        const selectedType = balanceTypes?.[type];
        return selectedType?.has_period === true;
      },
      then: (schema) =>
        schema.required("La date de début de période est obligatoire"),
      otherwise: (schema) => schema.optional(" "),
    }),
    periode_fin: dateSameOrAfter(
      "periode_debut",
      "La fin de période ne peut pas être antérieure au début",
    ).when("type", {
      is: (type) => {
        const selectedType = balanceTypes?.[type];
        return selectedType?.has_period === true;
      },
      then: (schema) =>
        schema.required("La date de fin de période est obligatoire"),
      // Masqués, les champs de période gardent leur valeur : l'ordre des dates ne doit
      // pas bloquer le formulaire sur une erreur que l'utilisateur ne voit pas.
      otherwise: () => dateMax().optional(" "),
    }),
    montant: number().required(" "),
    taux_tva: number(),
    type: string().required(" "),
    details: getBalanceDetailsSchema(),
    comment: stringMax(250).optional(),
    id_bien: number().optional(),
    id_tenant: number().optional(),
    id_property_folder: number().optional(),
    domaine: stringMax(250).optional(),
    files: array().of(object()),
    balances: array().of(
      object({
        domaine: stringMax(250).required(" "),
        id_bien: number().optional().nullable(),
        id_tenant: number().optional().nullable(),
        id_property_folder: number().optional().nullable(),
        montant: number().required(" "),
        affectation: stringMax(250).required(" "),
      }),
    ),
    affectation: stringMax(250).required(" "),
  });
};

// Schéma par défaut pour compatibilité (sans vérification has_period)
export const balanceSchema = object({
  date_transac: dateMax().required(" "),
  refacturation_values: array()
    .of(
      object({
        tenant_id: string().required(" "),
        date: dateMax().required(" "),
      }),
    )
    .optional(),
  periode_debut: dateMax().optional(" "),
  periode_fin: dateMax().optional(" "),
  montant: number().required(" "),
  taux_tva: number(),
  type: string().required(" "),
  details: balanceDetailsShape,
  comment: stringMax(250).optional(),
  id_bien: number().optional(),
  id_tenant: number().optional(),
  id_property_folder: number().optional(),
  domaine: stringMax(250).optional(),
  files: array().of(object()),
  balances: array().of(
    object({
      domaine: stringMax(250).required(" "),
      id_bien: number().optional().nullable(),
      id_tenant: number().optional().nullable(),
      id_property_folder: number().optional().nullable(),
      montant: number().required(" "),
      affectation: stringMax(250).required(" "),
    }),
  ),
  affectation: stringMax(250).required(" "),
});

export const balanceIV = {
  date_transac: "",
  periode_debut: "",
  periode_fin: "",
  montant: "",
  taux_tva: 0,
  type: "",
  details: { ...balanceDetailsIV },
  comment: "",
  id_bien: "",
  id_tenant: "",
  id_property_folder: "",
  domaine: "",
  files: [],
  affectation: "",
  refacturation_values: [],
};
Object.freeze(balanceIV);

export const balanceMasseValidationSchema = object({
  // L'édition groupée appelle `BalanceFields` sans `context`, donc `BalanceTargetFields`
  // ne s'affiche pas et rien n'écrit `affectation` sur ces lignes : sa validation ne
  // s'applique donc pas ici, à la différence des autres écrans qui réutilisent `balanceSchema`.
  balances: array().of(balanceSchema.omit(["affectation"])),
});

export const BALANCE_MASSE_INITIAL_VALUE = {
  balances: [{ ...balanceIV }],
};
